import React, { useContext, useEffect, useState } from "react";
import moment from "moment-timezone";
import { Box, Flex, Spinner } from "theme-ui";

import { getClientNotes, createNote, updateNote, deleteNote, pinNote, unpinNote } from "api/Notes";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import TextInput from "components/forms/TextInput";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { NotesIcon, PencilIcon, TrashIcon, QuestionMarkIcon } from "components/svg";
import { themevals } from "theme/themevals";
import { useToast } from "components/toast";
import UserContext from "context/User";

/**
 * MASTER_PLAN.md §3.9 work item #12 — Client Detail Notes tab.
 *
 * Mockup 21 describes pinned notes + tagged stream + composer with tag
 * and site pickers. First pass shipped the composer + plain
 * chronological stream. This pass (SESSION 45 audit findings
 * GAP-68 / GAP-69 / GAP-70 / GAP-71) adds pinning, tag chips, per-note
 * site link, and the filter row (`All / Pinned / <tag> / <tag>`), on
 * top of the backend columns added by migration
 * 1785400000000-AddNotePinningAndTags. Any author dropdown — mockup
 * 21's `Any author ▾` — is still deferred (needs a per-client
 * distinct-authors query that doesn't exist on the backend yet); the
 * rest matches the mockup.
 *
 * Pinning behavior:
 *   - Backend `GET /notes` already returns pinned first, so the list
 *     renders as (pinned section, then stream) with a hairline
 *     separator between the two sections — no client-side re-sort
 *     needed.
 *   - Any staff can pin / unpin (organizational action, not an edit
 *     to note content). Owner-only guard still applies to Edit and
 *     Delete as before.
 *
 * Edit / delete (added after Session 37 feedback — create-and-read-only
 * was shipped first, edit/delete were a real gap Jack hit in testing).
 * The backend already had PUT /notes/:id and DELETE /notes/:id; nothing
 * new needed there, just wiring:
 *   - Edit is restricted to `note.lastUpdateBy.id === current user.id`
 *     (NoteService.update throws ConflictException otherwise — an
 *     existing backend rule, not new here). The Edit action is hidden
 *     entirely for notes you don't own, rather than shown and erroring.
 *   - Delete (archive/soft-delete) has no such restriction on the
 *     backend — any staff member can archive any note — so the Delete
 *     action is shown for everyone, with a confirm step.
 */
export default function NotesTab({ clientId }: { clientId: string }) {
  const toast = useToast();
  const { user } = useContext<any>(UserContext);
  const [notes, setNotes] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  // SESSION 45 GAP-69 — tag input on the composer. Comma-separated so
  // it works with the existing TextInput without pulling in a chip-
  // input dependency. Backend accepts `tags?: string[]`, so the split
  // happens on submit and empty means no tags array is sent.
  const [newTags, setNewTags] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  // SESSION 45 GAP-71 — filter row. `null` = All; specific tag string
  // matches when the note's tags array contains that value; `pinned`
  // pseudo-filter matches pinned notes regardless of tag.
  const [filter, setFilter] = useState<string | null>(null);

  const loadNotes = async () => {
    setLoading(true);
    const res = await getClientNotes({
      data: { id: clientId },
      query: { recordsPer: 50, sortBy: "createdAt", sortDir: "desc" },
    });
    setNotes(Array.isArray(res?.items) ? res.items : []);
    setLoading(false);
  };

  useEffect(() => {
    if (clientId) loadNotes();
  }, [clientId]);

  const submitNote = async () => {
    if (!newNote.trim()) return;
    // SESSION 45 GAP-69 — split comma-separated tags on submit. Empty
    // string → undefined so the backend doesn't record an empty array.
    const parsedTags = newTags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    setSubmitting(true);
    const res = await createNote({
      data: {
        note: newNote.trim(),
        entityType: "Client",
        entityId: clientId,
        ...(parsedTags.length ? { tags: parsedTags } : {}),
      },
    });
    setSubmitting(false);
    if (res?.id) {
      setNewNote("");
      setNewTags("");
      loadNotes();
    } else {
      toast.add(res?.message || "Error adding note.", "var(--theme-ui-colors-danger)");
    }
  };

  const saveEdit = async (note: any, value: string) => {
    const res = await updateNote({ data: { id: note.id }, update: { note: value } });
    if (res?.id) {
      loadNotes();
      return true;
    }
    toast.add(res?.message || "Error updating note.", "var(--theme-ui-colors-danger)");
    return false;
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    const res = await deleteNote({ data: { id: deleteTarget.id } });
    setDeleting(false);
    setDeleteTarget(null);
    if (res?.success) {
      loadNotes();
    } else {
      toast.add(res?.message || "Error deleting note.", "var(--theme-ui-colors-danger)");
    }
  };

  // SESSION 45 GAP-68 — pin / unpin. Update state in place instead of
  // reloading the whole list; the backend response echoes the updated
  // row, so this is a single-row splice. Falls back to a reload if the
  // response shape is unexpected (network hiccup / permission failure).
  const togglePin = async (note: any) => {
    const wasPinned = !!note?.pinned;
    const res = wasPinned
      ? await unpinNote({ data: { id: note.id } })
      : await pinNote({ data: { id: note.id } });
    if (res?.id) {
      // Reload so the list re-sorts (pinned notes bubble to the top /
      // fall out). The N+1 concern doesn't apply at typical note counts.
      loadNotes();
    } else {
      toast.add(
        res?.message || (wasPinned ? "Error unpinning note." : "Error pinning note."),
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  // SESSION 45 GAP-71 — build the filter row. Tag options are derived
  // from what's actually attached to this client's notes rather than a
  // hard-coded `Corrigo / Billing / Relationship` list, so tags that
  // never got used don't clutter the row, and companies that use
  // different tag vocabularies still get useful filters.
  const availableTags: string[] = React.useMemo(() => {
    if (!notes) return [];
    const set = new Set<string>();
    for (const n of notes) {
      if (Array.isArray(n?.tags)) {
        for (const t of n.tags) {
          if (typeof t === "string" && t.trim()) set.add(t.trim());
        }
      }
    }
    return Array.from(set).sort();
  }, [notes]);

  const pinnedCount = React.useMemo(
    () => (notes ?? []).filter((n) => n?.pinned).length,
    [notes],
  );

  // Applied filter: null = show all; "pinned" = only pinned; other =
  // only notes with that tag.
  const visibleNotes = React.useMemo(() => {
    if (!notes) return [];
    if (filter === null) return notes;
    if (filter === "pinned") return notes.filter((n) => !!n?.pinned);
    return notes.filter((n) => Array.isArray(n?.tags) && n.tags.includes(filter));
  }, [notes, filter]);

  // Pinned section only renders when filter allows pinned notes
  // through (null or "pinned"). Split for rendering.
  const pinnedForRender = visibleNotes.filter((n) => !!n?.pinned);
  const streamForRender = visibleNotes.filter((n) => !n?.pinned);

  const totalCount = notes?.length ?? 0;
  const canSubmit = !!newNote.trim() && !submitting;

  return (
    <Box>
      <Flex
        sx={{
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: "14px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <Flex sx={{ alignItems: "baseline", gap: "10px", flexWrap: "wrap" }}>
          <H5
            fontFamily="Poppins"
            fontWeight={600}
            margin={0}
            padding={0}
            color={themevals.colors.black}
            fontSize={"15px"}
          >
            Notes
          </H5>
          {!loading && totalCount > 0 && (
            <Subtext
              fontSize={"12px"}
              fontFamily={"Poppins"}
              margin={0}
              color={themevals.colors.dkgrey}
            >
              {totalCount} · standing context for this client
            </Subtext>
          )}
          {!loading && totalCount === 0 && (
            <Subtext
              fontSize={"12px"}
              fontFamily={"Poppins"}
              margin={0}
              color={themevals.colors.dkgrey}
            >
              standing context for this client
            </Subtext>
          )}
        </Flex>
      </Flex>

      {/* ── Composer ─────────────────────────────────────────────── */}
      <Box
        sx={{
          background: "white",
          border: "1px solid",
          borderColor: "inputOutline",
          borderRadius: "8px",
          padding: "16px 20px",
          marginBottom: "20px",
          boxShadow: "0px 1px 2px 0px rgba(113, 125, 150, 0.03)",
        }}
      >
        <TextInput
          type="textarea"
          name="newNote"
          label="Add a note"
          rows={3}
          value={newNote}
          onChange={(val: any) => setNewNote(val)}
          placeholder="Standing context for this client — visible to all staff."
        />
        {/* SESSION 45 GAP-69 — tag input on the composer. Mockup 21
         * shows `+ Tag` and `+ Site` buttons; a full chip-picker is a
         * later polish, but comma-separated free text lets staff
         * classify notes today without a schema-locked enum. The
         * filter row derives its chips from what's actually attached,
         * so writing new tag vocabularies just works. Suggested tags
         * hinted in the placeholder are informed by the mockup's
         * examples (Corrigo / Billing / Relationship) but not enforced. */}
        <Box sx={{ marginTop: "10px" }}>
          <TextInput
            name="newTags"
            label="Tags (optional)"
            value={newTags}
            onChange={(val: any) => setNewTags(val)}
            placeholder="Comma-separated — e.g. Corrigo, Billing, Relationship"
          />
        </Box>
        <Flex
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "12px",
          }}
        >
          <Subtext
            fontSize={"11px"}
            fontFamily={"Poppins"}
            margin={0}
            color={themevals.colors.dkgrey}
          >
            {newNote.trim().length > 0
              ? `${newNote.trim().length} characters`
              : "Notes are visible to all staff on this client."}
          </Subtext>
          <BasicButton
            type="button"
            scheme="primary"
            onClick={submitNote}
            $submitting={submitting}
            disabled={!canSubmit}
          >
            Add Note
          </BasicButton>
        </Flex>
      </Box>

      {/* ── Stream ───────────────────────────────────────────────── */}
      {/* Filter row (SESSION 45 GAP-71) */}
      {!loading && (notes?.length ?? 0) > 0 && (
        <Flex
          sx={{
            gap: "8px",
            marginBottom: "16px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <FilterChip
            label={`All · ${notes?.length ?? 0}`}
            active={filter === null}
            onClick={() => setFilter(null)}
          />
          {pinnedCount > 0 && (
            <FilterChip
              label={`Pinned · ${pinnedCount}`}
              active={filter === "pinned"}
              onClick={() => setFilter("pinned")}
            />
          )}
          {availableTags.map((tag) => (
            <FilterChip
              key={tag}
              label={tag}
              active={filter === tag}
              onClick={() => setFilter(tag)}
            />
          ))}
        </Flex>
      )}

      {/* Stream */}
      {loading ? (
        <Box sx={{ textAlign: "center", paddingY: "40px" }}>
          <Spinner />
        </Box>
      ) : !notes || notes.length === 0 ? (
        <Box
          sx={{
            background: "white",
            border: "1px solid",
            borderColor: "inputOutline",
            borderRadius: "8px",
            padding: "48px 20px",
            textAlign: "center",
            boxShadow: "0px 1px 2px 0px rgba(113, 125, 150, 0.03)",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              width: "44px",
              height: "44px",
              borderRadius: "50%",
              background: "#F1EFE8",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "14px",
            }}
          >
            <NotesIcon fill={themevals.colors.dkgrey} height="20px" />
          </Box>
          <P
            color={themevals.colors.black}
            marginTop={0}
            marginBottom={"6px"}
            fontSize={"13px"}
            fontWeight={600}
          >
            No notes yet
          </P>
          <Box sx={{ maxWidth: "440px", marginX: "auto" }}>
            <P
              color={themevals.colors.dkgrey}
              marginTop={0}
              marginBottom={0}
              fontSize={"12px"}
              lineHeight={"18px"}
            >
              Notes are the place for standing context on this client — pricing
              agreements, communication preferences, quirks worth knowing before
              the next call.
            </P>
          </Box>
        </Box>
      ) : (
        <>
          {/* SESSION 45 GAP-68 — pinned section renders above the stream */}
          {pinnedForRender.length > 0 && (
            <Box sx={{ marginBottom: "20px" }}>
              <Flex sx={{ alignItems: "baseline", gap: "8px", marginBottom: "8px" }}>
                <Span
                  fontFamily={"Poppins"}
                  fontWeight={600}
                  fontSize={"12px"}
                  color={themevals.colors.dkgrey}
                  sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  📌 Pinned
                </Span>
                <Subtext
                  fontSize={"11px"}
                  fontFamily={"Poppins"}
                  margin={0}
                  color={themevals.colors.dkgrey}
                >
                  always visible at the top
                </Subtext>
              </Flex>
              <Box
                sx={{
                  background: "white",
                  border: "1px solid",
                  borderColor: "inputOutline",
                  borderRadius: "8px",
                  overflow: "hidden",
                  boxShadow: "0px 1px 2px 0px rgba(113, 125, 150, 0.03)",
                }}
              >
                {pinnedForRender.map((note: any, idx: number) => (
                  <NoteRow
                    key={note.id}
                    note={note}
                    isLast={idx === pinnedForRender.length - 1}
                    canEdit={!!user?.id && note?.lastUpdateBy?.id === user.id}
                    onSaveEdit={(value) => saveEdit(note, value)}
                    onDelete={() => setDeleteTarget(note)}
                    onTogglePin={() => togglePin(note)}
                  />
                ))}
              </Box>
            </Box>
          )}

          {streamForRender.length > 0 && (
            <Box>
              {pinnedForRender.length > 0 && (
                <Flex sx={{ alignItems: "baseline", gap: "8px", marginBottom: "8px" }}>
                  <Span
                    fontFamily={"Poppins"}
                    fontWeight={600}
                    fontSize={"12px"}
                    color={themevals.colors.dkgrey}
                    sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
                  >
                    Recent
                  </Span>
                </Flex>
              )}
              <Box
                sx={{
                  background: "white",
                  border: "1px solid",
                  borderColor: "inputOutline",
                  borderRadius: "8px",
                  overflow: "hidden",
                  boxShadow: "0px 1px 2px 0px rgba(113, 125, 150, 0.03)",
                }}
              >
                {streamForRender.map((note: any, idx: number) => (
                  <NoteRow
                    key={note.id}
                    note={note}
                    isLast={idx === streamForRender.length - 1}
                    canEdit={!!user?.id && note?.lastUpdateBy?.id === user.id}
                    onSaveEdit={(value) => saveEdit(note, value)}
                    onDelete={() => setDeleteTarget(note)}
                    onTogglePin={() => togglePin(note)}
                  />
                ))}
              </Box>
            </Box>
          )}

          {pinnedForRender.length === 0 && streamForRender.length === 0 && (
            <Box
              sx={{
                background: "white",
                border: "1px solid",
                borderColor: "inputOutline",
                borderRadius: "8px",
                padding: "32px 20px",
                textAlign: "center",
                boxShadow: "0px 1px 2px 0px rgba(113, 125, 150, 0.03)",
              }}
            >
              <Subtext
                fontSize={"12px"}
                fontFamily={"Poppins"}
                margin={0}
                color={themevals.colors.dkgrey}
              >
                No notes match this filter. Try{" "}
                <Box
                  as="button"
                  onClick={() => setFilter(null)}
                  sx={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    color: themevals.colors.primary,
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontFamily: "Poppins",
                    fontSize: "12px",
                  }}
                >
                  All
                </Box>
                .
              </Subtext>
            </Box>
          )}
        </>
      )}

      {deleteTarget && (
        <BasicModal
          title="Delete this note?"
          cancel_click={() => setDeleteTarget(null)}
          cancel_text="No, cancel"
          size="sm"
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          confirm_click={confirmDelete}
          confirm_text="Yes, delete"
          submitting={deleting}
        >
          <P>Are you sure you want to delete this note? This can&apos;t be undone.</P>
        </BasicModal>
      )}
    </Box>
  );
}

function NoteRow({
  note,
  isLast,
  canEdit,
  onSaveEdit,
  onDelete,
  onTogglePin,
}: {
  note: any;
  isLast: boolean;
  canEdit: boolean;
  onSaveEdit: (value: string) => Promise<boolean>;
  onDelete: () => void;
  onTogglePin: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(note.note ?? "");
  const [saving, setSaving] = useState(false);

  const author =
    note?.lastUpdateBy?.firstName || note?.createdBy?.firstName
      ? `${note?.lastUpdateBy?.firstName || note?.createdBy?.firstName} ${note?.lastUpdateBy?.lastName || note?.createdBy?.lastName || ""}`.trim()
      : "Staff";
  const initials = getInitials(author);

  const startEdit = () => {
    setEditValue(note.note ?? "");
    setIsEditing(true);
  };

  const save = async () => {
    if (!editValue.trim()) return;
    setSaving(true);
    const ok = await onSaveEdit(editValue.trim());
    setSaving(false);
    if (ok) setIsEditing(false);
  };

  return (
    <Flex
      sx={{
        alignItems: "flex-start",
        gap: "14px",
        padding: "16px 20px",
        borderBottom: isLast ? "none" : "1px solid",
        borderColor: "inputOutline",
      }}
    >
      <Flex
        sx={{
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          background: "#F1EFE8",
          color: themevals.colors.dkgrey,
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Poppins",
          fontSize: "11px",
          fontWeight: 600,
          flexShrink: 0,
          marginTop: "2px",
        }}
      >
        {initials}
      </Flex>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Flex
          sx={{
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: "10px",
            marginBottom: "6px",
            flexWrap: "wrap",
          }}
        >
          <Span
            fontFamily={"Poppins"}
            fontWeight={600}
            fontSize={"13px"}
            color={themevals.colors.black}
          >
            {author}
          </Span>
          <Flex sx={{ alignItems: "center", gap: "14px" }}>
            <Subtext
              fontSize={"11px"}
              fontFamily={"Poppins"}
              margin={0}
              color={themevals.colors.dkgrey}
            >
              {moment(note.createdAt).format("MMM D, YYYY · h:mm A")}
            </Subtext>
            {!isEditing && (
              <Flex sx={{ gap: "10px", alignItems: "center" }}>
                {/* SESSION 45 GAP-68 — pin toggle. Any staff can
                 * pin/unpin, so no `canEdit` guard. Icon-only, tooltip
                 * via aria-label. */}
                <Box
                  as="button"
                  onClick={onTogglePin}
                  aria-label={note?.pinned ? "Unpin note" : "Pin note"}
                  title={note?.pinned ? "Unpin note" : "Pin note"}
                  sx={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "13px",
                    opacity: note?.pinned ? 1 : 0.5,
                    filter: note?.pinned ? "none" : "grayscale(1)",
                    "&:hover": { opacity: 1, filter: "none" },
                  }}
                >
                  📌
                </Box>
                {canEdit && (
                  <Box
                    as="button"
                    onClick={startEdit}
                    aria-label="Edit note"
                    sx={{
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      opacity: 0.6,
                      "&:hover": { opacity: 1 },
                    }}
                  >
                    <PencilIcon height="12px" fill={themevals.colors.dkgrey} />
                  </Box>
                )}
                <Box
                  as="button"
                  onClick={onDelete}
                  aria-label="Delete note"
                  sx={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    opacity: 0.6,
                    "&:hover": { opacity: 1 },
                  }}
                >
                  <TrashIcon height="12px" fill={themevals.colors.dkgrey} />
                </Box>
              </Flex>
            )}
          </Flex>
        </Flex>

        {isEditing ? (
          <Box>
            <TextInput
              type="textarea"
              name={`edit-note-${note.id}`}
              rows={3}
              value={editValue}
              onChange={(val: any) => setEditValue(val)}
            />
            <Flex sx={{ gap: "10px", marginTop: "8px" }}>
              <BasicButton
                type="button"
                scheme="primary"
                onClick={save}
                $submitting={saving}
                disabled={!editValue.trim()}
              >
                Save
              </BasicButton>
              <BasicButton
                type="button"
                scheme="secondaryOutline"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </BasicButton>
            </Flex>
          </Box>
        ) : (
          <>
            <P
              fontFamily={"Poppins"}
              fontSize={"13px"}
              color={themevals.colors.black}
              marginTop={0}
              marginBottom={0}
              lineHeight={"20px"}
              style={{ whiteSpace: "pre-wrap" }}
            >
              {note.note}
            </P>
            {/* SESSION 45 GAP-69 / GAP-70 — tags row + site link.
             * Renders only when the note carries either. Site link
             * shows the LCP name (from the hydrated `siteLcp` relation)
             * so a client-scoped note can still call out the specific
             * site it references. */}
            {(Array.isArray(note?.tags) && note.tags.length > 0) || note?.siteLcp?.name ? (
              <Flex
                sx={{
                  gap: "6px",
                  marginTop: "8px",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {Array.isArray(note?.tags) &&
                  note.tags.map((tag: string) => (
                    <Span
                      key={tag}
                      fontFamily={"Poppins"}
                      fontSize={"10px"}
                      fontWeight={600}
                      color={themevals.colors.dkgrey}
                      sx={{
                        padding: "2px 8px",
                        borderRadius: "10px",
                        background: "#F1EFE8",
                        textTransform: "none",
                        letterSpacing: "0.2px",
                      }}
                    >
                      {tag}
                    </Span>
                  ))}
                {note?.siteLcp?.name && (
                  <Subtext
                    fontSize={"11px"}
                    fontFamily={"Poppins"}
                    margin={0}
                    color={themevals.colors.dkgrey}
                  >
                    site: {note.siteLcp.name}
                  </Subtext>
                )}
              </Flex>
            ) : null}
            {/* SESSION 45 GAP-68 — pin audit line, only on pinned
             * notes. Mockup 21: `Pinned by Jack · Jan 12, 2026`. */}
            {note?.pinned && note?.pinnedAt && (
              <Subtext
                fontSize={"11px"}
                fontFamily={"Poppins"}
                margin={0}
                marginTop={"6px"}
                color={themevals.colors.dkgrey}
              >
                Pinned{
                  note?.pinnedBy?.firstName
                    ? ` by ${note.pinnedBy.firstName}${note.pinnedBy.lastName ? " " + note.pinnedBy.lastName : ""}`
                    : ""
                } · {moment(note.pinnedAt).format("MMM D, YYYY")}
              </Subtext>
            )}
          </>
        )}
      </Box>
    </Flex>
  );
}

function getInitials(name: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * SESSION 45 GAP-71 — filter chip for the Notes tab filter row.
 * Same visual language as chips elsewhere (Batch cards, RFQ list),
 * but rendered as a real button so keyboard tab order works.
 */
function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      as="button"
      onClick={onClick}
      sx={{
        background: active ? themevals.colors.black : "white",
        color: active ? "white" : themevals.colors.dkgrey,
        border: "1px solid",
        borderColor: active ? themevals.colors.black : "inputOutline",
        borderRadius: "14px",
        padding: "4px 12px",
        fontSize: "12px",
        fontFamily: "Poppins",
        fontWeight: 500,
        cursor: "pointer",
        transition: "background 120ms ease, color 120ms ease, border-color 120ms ease",
        whiteSpace: "nowrap",
        "&:hover": active
          ? undefined
          : { borderColor: themevals.colors.dkgrey },
      }}
    >
      {label}
    </Box>
  );
}
